import * as THREE from 'three';

/** 1 m ground grid. Yaw locks to 90°. Place sits ~2.2 m in front of Rowan. */
export const SNAP_GRID = 1;
export const SNAP_YAW_STEP = Math.PI / 2;
export const SNAP_PLACE_DIST = 2.2;
export const CHARGE_RANGE = 1.6;
/** Two hits collapse a fresh piece (1 − 0.55 − 0.55). */
export const CHARGE_DAMAGE = 0.55;
export const CHARGE_COOLDOWN = 1;
export const PIECE_CAP = 40;

export const CHARGE_KINDS = ['trike', 'rex', 'carno', 'stego'] as const;

export type ChargeKind = (typeof CHARGE_KINDS)[number];
export type PieceKind = 'lean-to' | 'workbench' | 'drying-rack' | 'spike-wall';

export type SnapPose = { x: number; y: number; z: number; yaw: number };

export type ChargeSource = {
  id?: string;
  x: number;
  z: number;
  kind: string;
  charging: boolean;
};

export type SnapPiece = {
  id: string;
  kind: PieceKind;
  x: number;
  y: number;
  z: number;
  yaw: number;
  integrity: number;
  mesh?: THREE.Object3D;
};

export const PIECE_COST: Record<PieceKind, { wood: number; fiber: number }> = {
  'lean-to': { wood: 6, fiber: 4 },
  workbench: { wood: 4, fiber: 0 },
  'drying-rack': { wood: 3, fiber: 2 },
  'spike-wall': { wood: 5, fiber: 0 },
};

const BARK = 0x5a3a22;
const THATCH = 0x8a7040;
const LASH = 0x6a5a38;
const GHOST = 0x7a9a58;

export function isPieceKind(value: string): value is PieceKind {
  return value === 'lean-to' || value === 'workbench' || value === 'drying-rack' || value === 'spike-wall';
}

export function isChargeKind(kind: string): kind is ChargeKind {
  return kind === 'trike' || kind === 'rex' || kind === 'carno' || kind === 'stego';
}

export function snapAxis(n: number): number {
  const snapped = Math.round(n / SNAP_GRID) * SNAP_GRID;
  return snapped === 0 ? 0 : snapped;
}

export function snapYaw(yaw: number): number {
  const twoPi = Math.PI * 2;
  let wrapped = yaw % twoPi;
  if (wrapped < 0) {
    wrapped += twoPi;
  }
  const snapped = Math.round(wrapped / SNAP_YAW_STEP) * SNAP_YAW_STEP;
  const out = snapped % twoPi;
  return out === 0 ? 0 : out;
}

export function placeInFront(
  player: { x: number; z: number },
  yaw: number,
  dist = SNAP_PLACE_DIST,
): SnapPose {
  const fx = -Math.sin(yaw);
  const fz = -Math.cos(yaw);
  return {
    x: snapAxis(player.x + fx * dist),
    y: 0,
    z: snapAxis(player.z + fz * dist),
    yaw: snapYaw(yaw),
  };
}

export function applySnapPose(obj: THREE.Object3D, pose: SnapPose): void {
  obj.position.set(pose.x, pose.y, pose.z);
  obj.rotation.y = pose.yaw;
}

export function hitIntegrity(current: number, damage = CHARGE_DAMAGE): number {
  return Math.max(0, Math.min(1, current - damage));
}

export function chargeHitsPiece(piece: { x: number; z: number }, dino: ChargeSource, range = CHARGE_RANGE): boolean {
  if (!dino.charging || !isChargeKind(dino.kind)) {
    return false;
  }
  return Math.hypot(dino.x - piece.x, dino.z - piece.z) <= range;
}

export function createCampPiece(kind: Exclude<PieceKind, 'lean-to'>, ghost = false): THREE.Group {
  if (kind === 'workbench') {
    return createWorkbench(ghost);
  }
  if (kind === 'drying-rack') {
    return createDryingRack(ghost);
  }
  return createSpikeWall(ghost);
}

export class SnapYard {
  readonly pieces: SnapPiece[] = [];
  private seq = 1;
  private readonly cool = new Map<string, number>();

  get count(): number {
    return this.pieces.length;
  }

  canPlace(): boolean {
    return this.pieces.length < PIECE_CAP;
  }

  leanTo(): SnapPiece | undefined {
    return this.pieces.find((piece) => piece.kind === 'lean-to');
  }

  place(kind: PieceKind, pose: SnapPose, mesh?: THREE.Object3D): SnapPiece | null {
    if (!this.canPlace()) {
      return null;
    }
    const piece: SnapPiece = {
      id: `${kind}-${this.seq}`,
      kind,
      x: snapAxis(pose.x),
      y: pose.y,
      z: snapAxis(pose.z),
      yaw: snapYaw(pose.yaw),
      integrity: 1,
    };
    this.seq += 1;
    if (mesh) {
      piece.mesh = mesh;
      applySnapPose(mesh, piece);
    }
    this.pieces.push(piece);
    return piece;
  }

  remove(id: string): SnapPiece | null {
    const index = this.pieces.findIndex((piece) => piece.id === id);
    if (index < 0) {
      return null;
    }
    return this.pieces.splice(index, 1)[0] ?? null;
  }

  removeKind(kind: PieceKind): SnapPiece[] {
    const gone: SnapPiece[] = [];
    for (let i = this.pieces.length - 1; i >= 0; i--) {
      const piece = this.pieces[i];
      if (piece?.kind === kind) {
        this.pieces.splice(i, 1);
        gone.push(piece);
      }
    }
    return gone;
  }

  /** Charging trike/rex/carno/stego within 1.6 m subtracts integrity. Returns pieces that hit 0. */
  tickCharges(dinos: readonly ChargeSource[], dt: number): SnapPiece[] {
    for (const [id, left] of this.cool) {
      const next = left - dt;
      if (next <= 0) {
        this.cool.delete(id);
      } else {
        this.cool.set(id, next);
      }
    }

    for (const dino of dinos) {
      if (!dino.charging || !isChargeKind(dino.kind)) {
        continue;
      }
      const key = dino.id ?? `${dino.kind}:${dino.x}:${dino.z}`;
      if ((this.cool.get(key) ?? 0) > 0) {
        continue;
      }
      let struck = false;
      for (const piece of this.pieces) {
        if (!chargeHitsPiece(piece, dino)) {
          continue;
        }
        piece.integrity = hitIntegrity(piece.integrity);
        struck = true;
      }
      if (struck) {
        this.cool.set(key, CHARGE_COOLDOWN);
      }
    }

    const broken: SnapPiece[] = [];
    for (let i = this.pieces.length - 1; i >= 0; i--) {
      const piece = this.pieces[i];
      if (piece && piece.integrity <= 0) {
        this.pieces.splice(i, 1);
        broken.push(piece);
      }
    }
    return broken;
  }
}

function createWorkbench(ghost: boolean): THREE.Group {
  const root = new THREE.Group();
  root.name = ghost ? 'CC-WORKBENCH-GHOST' : 'CC-WORKBENCH';
  root.userData.id = root.name;
  const bark = makeMat(BARK, 0.94, ghost);
  const top = makeMat(THATCH, 0.9, ghost);
  pole(root, bark, -0.42, 0.36, -0.24, 0.72, 0.04);
  pole(root, bark, 0.42, 0.36, -0.24, 0.72, 0.04);
  pole(root, bark, -0.42, 0.36, 0.24, 0.72, 0.04);
  pole(root, bark, 0.42, 0.36, 0.24, 0.72, 0.04);
  const slab = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.07, 0.62), top);
  slab.position.set(0, 0.74, 0);
  root.add(slab);
  const lip = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.22, 0.06), bark);
  lip.position.set(0, 0.86, -0.28);
  root.add(lip);
  return root;
}

function createDryingRack(ghost: boolean): THREE.Group {
  const root = new THREE.Group();
  root.name = ghost ? 'CC-DRYING-RACK-GHOST' : 'CC-DRYING-RACK';
  root.userData.id = root.name;
  const bark = makeMat(BARK, 0.94, ghost);
  const lash = makeMat(LASH, 0.88, ghost);
  pole(root, bark, -0.55, 0.7, 0, 1.4, 0.035);
  pole(root, bark, 0.55, 0.7, 0, 1.4, 0.035);
  pole(root, lash, 0, 1.22, 0, 0.04, 0.025, 1.15, Math.PI / 2);
  pole(root, lash, 0, 0.88, 0, 0.04, 0.022, 1.15, Math.PI / 2);
  pole(root, lash, 0, 0.54, 0, 0.04, 0.022, 1.15, Math.PI / 2);
  return root;
}

function createSpikeWall(ghost: boolean): THREE.Group {
  const root = new THREE.Group();
  root.name = ghost ? 'CC-SPIKE-WALL-GHOST' : 'CC-SPIKE-WALL';
  root.userData.id = root.name;
  const bark = makeMat(BARK, 0.94, ghost);
  pole(root, bark, 0, 0.12, 0, 0.08, 0.05, 1.7, Math.PI / 2);
  for (const x of [-0.7, -0.35, 0, 0.35, 0.7]) {
    const stake = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.045, 1.15, 5), bark);
    stake.position.set(x, 0.62, 0);
    stake.rotation.x = -0.18;
    root.add(stake);
  }
  return root;
}

function makeMat(color: number, roughness: number, ghost: boolean): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: ghost ? GHOST : color,
    roughness,
    metalness: 0.03,
    transparent: ghost,
    opacity: ghost ? 0.34 : 1,
    depthWrite: !ghost,
  });
}

function pole(
  parent: THREE.Object3D,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  height: number,
  radius: number,
  length = height,
  rotZ = 0,
): void {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius * 1.12, length, 5), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.z = rotZ;
  parent.add(mesh);
}
